import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback, useMemo } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { GENERIC_ERROR_MODAL } from "src/apps/AppFlow/pages/hooks/usePartialSubmit";
import { styles as GENERAL_STYLES } from "src/apps/AppFlow/pages/styles";
import {
  Checkbox,
  FormFlowPage,
  InfoAlert,
  TextInput,
  TextInputWithLoading,
  TextLink,
  Touchable,
} from "src/components";
import { Button, LightText } from "src/components/v1";
import { useModal } from "src/hooks";
import useMergedRef from "src/hooks/useMergedRef";
import { UserPlus, X } from "src/icons";
import { createStyles } from "src/styles";
import { Applicant } from "src/types/Applicant";
import asyncZodResolver from "src/utilities/asyncZodResolver";
import { noInput } from "src/utilities/input";
import { strictEmailValidate } from "src/validators";
import { v4 as uuid } from "uuid";
import * as z from "zod";

import { colors, uniqueBy } from "@fraction/shared";
import cloneDeep from "clone-deep";
import _ from "lodash";

export interface ApplicantsProps {
  onChange: (value: Applicant[]) => void;
  value?: Applicant[];

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;

  valuesToShow?: Record<"phone" | "email" | "name", boolean>;
  children?: ReactNode;
}

interface ApplicantContactProps {
  onChange: (index: number, value: Applicant, key: string) => void;
  onDelete?: (index: number) => void;
  value?: Applicant;
  index: number;
  errors?: FieldErrors<any>;

  focusRef?: Ref<HTMLInputElement>;
  register: Ref<HTMLInputElement>;

  valuesToShow: Record<"phone" | "email" | "name", boolean>;
  validating?: boolean;
}

const styles = createStyles({
  alert: {
    marginBottom: 24,
    maxWidth: "100%",
  },
  container: {
    marginBottom: 24,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  nowrap: {
    flexWrap: "nowrap",
  },
  button: {
    marginLeft: 20,
    // This is to make up for the fact that the inputs have lots of "blank"
    // space at the bottom which makes the button look un-aligned without this margin.
    marginBottom: 30,
  },
  input: {
    width: "fit-content",
    minWidth: 265,
    "@media(max-width: 800px)": {
      width: "100%",
    },
  },
  inputContainer: {
    "&:not(:last-child)": {
      marginRight: 20,
      "@media(max-width: 800px)": {
        marginRight: 0,
      },
    },
    "@media(max-width: 800px)": {
      width: "100%",
    },
  },
  checkbox: {
    marginTop: 28,
    marginBottom: 6,
  },
  label: {
    fontSize: 14,
    textAlign: "left",
    color: colors.BLACK_TEXT,
    letterSpacing: "0.01em",
    lineHeight: "145%",
  },
});

const RAW_STYLES = {
  userPlus: {
    marginLeft: 11,
  },
};

const ApplicantContact = memo(
  ({
    errors,
    index,
    onChange,
    onDelete,
    value,
    register,
    focusRef,
    valuesToShow,
    validating = false,
  }: ApplicantContactProps) => {
    const merged = useMergedRef(register, focusRef);
    const ref = focusRef ? merged : register;

    const handleChangeEmail = useCallback(
      (email: string) => {
        onChange(
          index,
          {
            ...value,
            email: {
              ...value?.email,
              email,
            },
          },
          "email"
        );
      },
      [index, value, onChange]
    );

    const handleChangeName = useCallback(
      (name: string) => {
        onChange(
          index,
          {
            ...value,
            name,
          },
          "name"
        );
      },
      [index, value, onChange]
    );

    const handleChangePhone = useCallback(
      (phone: string) => {
        onChange(
          index,
          {
            ...value,
            phoneAndContactMethodPreferences: { ...value?.phoneAndContactMethodPreferences, phone },
          },
          "phone"
        );
      },
      [index, value, onChange]
    );

    const handleDelete = useCallback(() => {
      onDelete?.(index);
    }, [index, onDelete]);

    return (
      <div css={[styles.row, styles.nowrap]}>
        <div css={styles.row}>
          {valuesToShow.name && (
            <TextInput
              error={errors?.name?.message}
              ref={ref}
              inputStyle={styles.input}
              style={[GENERAL_STYLES.input, styles.inputContainer]}
              name={`name-${index}`}
              label="Full legal name"
              defaultValue={value?.name || ""}
              onChange={handleChangeName}
              autoComplete="name"
            />
          )}
          {valuesToShow.email && (
            <TextInputWithLoading
              error={errors?.email?.message}
              inputStyle={styles.input}
              style={[GENERAL_STYLES.input, styles.inputContainer]}
              name={`email-${index}`}
              label="Email"
              placeholder="contact@fraction.com"
              defaultValue={value?.email?.email || ""}
              onChange={handleChangeEmail}
              type="email"
              autoComplete="email"
              ref={register}
              loading={validating}
            />
          )}
          {valuesToShow.phone && (
            <TextInput
              error={errors?.phone?.message}
              ref={register}
              inputStyle={styles.input}
              style={[GENERAL_STYLES.input, styles.inputContainer]}
              mask={["(999) 999-9999"]}
              name={`phone-${index}`}
              label="Phone number"
              placeholder="999 999-9999"
              defaultValue={value?.phoneAndContactMethodPreferences?.phone || ""}
              onChange={handleChangePhone}
              type="tel-national"
              autoComplete="tel-national"
            />
          )}
        </div>
        {index !== 0 && (
          <Touchable css={styles.button} onClick={handleDelete}>
            <X height={28} width={28} color={colors.ICON} />
          </Touchable>
        )}
      </div>
    );
  }
);

const Applicants = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  valuesToShow = { email: true, name: true, phone: false },
  children,
  ...props
}: ApplicantsProps) => {
  const { showModal } = useModal();
  const validate = useCallback(
    () =>
      // First applicant's info is required
      !noInput(value?.[0]?.email?.email) &&
      !noInput(value?.[0]?.name) &&
      // No applicant can be partially filled
      !value?.some((applicant) => noInput(applicant?.email?.email) !== noInput(applicant?.name)),
    [value]
  );
  const on4XXError = useCallback(() => showModal(GENERIC_ERROR_MODAL), [showModal]);
  const {
    handleNext: partialSubmit,
    nextDisabled,
    nextLoading,
  } = usePartialSubmit({ onNext, validate, on4XXError });

  const schema = useMemo(() => {
    const schemaShape: any = {};
    for (let index = 0; index < (value?.length || 0); index++) {
      if (valuesToShow.phone) {
        schemaShape[`phone-${index}`] = z.string();
      }
      if (valuesToShow.name) {
        schemaShape[`name-${index}`] = z.string().min(5, {
          message: "Please enter both your first and last names.",
        });
      }
      if (valuesToShow.email) {
        schemaShape[`email-${index}`] = strictEmailValidate;
      }
    }
    return z.object(schemaShape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.length]);

  const { register, handleSubmit, errors, formState, clearErrors, setError } = useForm({
    resolver: asyncZodResolver(schema),
    reValidateMode: "onSubmit",
  });

  const handleChangeContact = useCallback(
    (index: number, applicant: Applicant, key: string) => {
      const newApplicants = [...(value || [])];
      newApplicants[index] = { ...(newApplicants[index] || {}), ...applicant };
      onChange?.(newApplicants);
      clearErrors(`${key}-${index}`);
    },
    [value, onChange, clearErrors]
  );

  const handleOptedInToMarketingChange = useCallback(
    (checked: boolean) => {
      // We save the marketing opt-in status on the first applicant's user.
      const newApplicants = [...(value || [])];
      if (!newApplicants[0] || !newApplicants[0].email) {
        return;
      }
      newApplicants[0].email.optedIn = checked ? new Date() : null;
      onChange?.(newApplicants);
    },
    [value, onChange]
  );

  const handleNext = useCallback(() => {
    const uniqueApplicants = uniqueBy(value || [], (app) => app?.email?.email);

    if (uniqueApplicants.length < (value?.length || 0)) {
      const sortedByEmails = _.sortBy(cloneDeep(value) || [], "email.email");
      // set the email to <email>+<count>@<domain> for any duplicates e.g. josh+1@fraction.com
      let lastSeenEmail: string | undefined = undefined;
      let lastSeenCount = 0;
      for (let i = 0; i < sortedByEmails.length; i++) {
        const email = sortedByEmails[i].email?.email;
        if (!!email && email === lastSeenEmail) {
          const splitEmail = email.split("@");
          const applicantToModify = value?.find((applicant) => applicant.id === sortedByEmails[i].id);
          if (applicantToModify?.email?.email) {
            applicantToModify.email.email = `${splitEmail[0]}+${lastSeenCount + 1}@${splitEmail[1]}`;
          }
          lastSeenCount++;
        }
        lastSeenEmail = email;
      }
      if (value) {
        onChange(value);
      }
      setTimeout(() => {
        partialSubmit();
      }, 300);
    } else {
      partialSubmit();
    }
  }, [value, setError, partialSubmit]);

  const handleAddContact = useCallback(() => {
    onChange([...(value || []), { id: uuid(), priority: value?.length || 0 }]);
  }, [value, onChange]);

  const handleDeleteContact = useCallback(
    (index: number) => {
      const newApplicants = [...(value || [])];
      newApplicants?.splice(index, 1);
      onChange(newApplicants);
    },
    [value, onChange]
  );

  return (
    <div>
      <FormFlowPage
        {...props}
        title="Please list all of the people on your application."
        description="You should include everyone that's on title, including yourself. If you are married, it's also helpful to include your spouse."
        onNext={handleSubmit(handleNext)}
        onBack={onBack}
        style={style}
        nextDisabled={nextDisabled}
        nextLoading={nextLoading || formState.isSubmitting}
      >
        <div css={styles.container}>
          <>
            {value?.map((applicant, index) => (
              <ApplicantContact
                key={index}
                valuesToShow={valuesToShow}
                register={register}
                focusRef={index === 0 ? focusRef : undefined}
                errors={{
                  name: errors?.[`name-${index}`],
                  phone: errors?.[`phone-${index}`],
                  email: errors?.[`email-${index}`],
                }}
                value={applicant}
                index={index}
                onDelete={handleDeleteContact}
                onChange={handleChangeContact}
                validating={formState.isValidating || formState.isSubmitting}
              />
            ))}
          </>
          <Button narrow onClick={handleAddContact}>
            Add applicant
            <UserPlus style={RAW_STYLES.userPlus} color="white" height={18} width={18} />
          </Button>
          <div css={styles.checkbox}>
            <Checkbox onChange={handleOptedInToMarketingChange}>
              <LightText style={styles.label}>
                By checking this box, I agree to receive recurring automated promotional and personalized
                marketing communications.&nbsp;
                <TextLink underlined href="https://www.fraction.com/usa/privacy-policy">
                  View Privacy Policy
                </TextLink>
              </LightText>
            </Checkbox>
          </div>
        </div>
        <InfoAlert style={styles.alert}>
          We require your full legal name to match your documentation and property records.{" "}
          <b>We will never share your email</b> and will only email you to communicate about your application.
        </InfoAlert>
        {children}
      </FormFlowPage>
    </div>
  );
};

export default memo(Applicants);
